import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFaceSmile,
  faFaceMeh,
  faFaceFrown,
  faFaceFrownSlight,
  faFaceGrinBeam,
} from '@fortawesome/pro-solid-svg-icons';
import {
  SurveyResponse,
  WellnessResponse,
} from '../../../../../shared/constants';
import {
  backgroundActive,
  customError,
  dimGrey,
  inactive,
  lightBlack,
  lightTeal,
  orange,
  successBg,
  warningBg,
  warningYellow,
} from '../../../../../mainTheme';

type ChipResponse = SurveyResponse | WellnessResponse;

interface SurveyResponseChipProps {
  response: ChipResponse | string;
  isCurrent?: boolean;
  style?: React.CSSProperties;
  highlightBorder?: boolean;
}

// One entry per label the backend can return: CSAT (1-4) and daily wellness
// check (1-5) scales share this chip.
const configMap: Record<
  ChipResponse,
  { icon: any; bg: string; iconColor: string }
> = {
  [SurveyResponse.VeryUnhappy]: {
    icon: faFaceFrown,
    bg: '#FCEFEE',
    iconColor: customError,
  },
  [SurveyResponse.SomewhatUnhappy]: {
    icon: faFaceMeh,
    bg: '#FFF4EB',
    iconColor: orange,
  },
  [SurveyResponse.SomewhatHappy]: {
    icon: faFaceSmile,
    bg: backgroundActive,
    iconColor: lightTeal,
  },
  [SurveyResponse.VeryHappy]: {
    icon: faFaceGrinBeam,
    bg: successBg,
    iconColor: '#41AF60',
  },
  [WellnessResponse.VeryBad]: {
    icon: faFaceFrown,
    bg: '#FCEFEE',
    iconColor: customError,
  },
  [WellnessResponse.Bad]: {
    icon: faFaceFrownSlight,
    bg: '#FFF4EB',
    iconColor: orange,
  },
  [WellnessResponse.Neutral]: {
    icon: faFaceMeh,
    bg: warningBg,
    iconColor: warningYellow,
  },
  [WellnessResponse.Good]: {
    icon: faFaceSmile,
    bg: backgroundActive,
    iconColor: lightTeal,
  },
  [WellnessResponse.VeryGood]: {
    icon: faFaceGrinBeam,
    bg: successBg,
    iconColor: '#41AF60',
  },
};

const UNKNOWN_RESPONSE_CONFIG = {
  icon: faFaceMeh,
  bg: inactive,
  iconColor: dimGrey,
};

export const SurveyResponseChip: React.FC<SurveyResponseChipProps> = ({
  response,
  isCurrent,
  style,
  highlightBorder,
}) => {
  // Unknown/empty labels (e.g. an unmapped score from an older backend) get a
  // neutral chip rather than masquerading as a real response.
  const config = configMap[response as ChipResponse] || UNKNOWN_RESPONSE_CONFIG;
  const label = response || 'N/A';

  return (
    <span
      style={{
        display: 'flex',
        height: 24,
        padding: '4px 12px 4px 6px',
        alignItems: 'center',
        gap: '4px',
        flexShrink: 0,

        borderRadius: 16,
        background: config.bg,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: '16px',
        color: isCurrent ? lightBlack : '#9999A3',
        width: 'fit-content',
        ...(isCurrent &&
          highlightBorder && {
            border: `1.5px solid ${config.iconColor}`,
          }),
        ...style,
      }}
    >
      <FontAwesomeIcon
        icon={config.icon}
        color={config.iconColor}
        style={{ marginRight: 6, fontSize: 16 }}
      />
      {label}
    </span>
  );
};

export default SurveyResponseChip;
